export interface Match {
  id: string;
  match_date: string;
  status: string;
  status_label: string;
  feedback?: string;
  is_ai_chat_initiated?: boolean
  // Apr-17 Phase 2b: primary_goal reciprocity flag surfaced in the dashboard
  // match list. true = Primary (direct value exchange), false = Adjacent
  // (SOFT-fallback backfill), null = not evaluable (legacy, unknown goal).
  // Fed from backend matches.reciprocal column (commit 1730a9f).
  reciprocal?: boolean | null;
  // Apr-25: score tier now returned by listMatches so MatchCard
  // can display them in the collapsed state. Optional + nullable for
  // backwards-compatibility with cached responses from before the backend
  // change rolled out.
  compatibility_score?: number | null;
  match_tier?: 'perfect' | 'strong' | 'worth_exploring' | 'low' | null;
  /** Multi-event: event this match belongs to (null for legacy/global). */
  event_id?: string | null;
  event_name?: string | null;
  other_user: {
    id: string;
    name: string;
    designation: string | null;
    objective: string;
    // F6: match location (city = physical location, region = target-market
    // region display string like "US, Europe"). null when unknown.
    city?: string | null;
    region?: string | null;
    // Multi-event: goals the matched user set FOR THIS event (slugs, e.g.
    // ['find_customers']). Empty/null = they use their profile objective.
    // When present, this is their authoritative intent for this event.
    event_goals?: string[] | null;
  };
}

export type StatusColor =
  | 'warning'
  | 'info'
  | 'success'
  | 'error'
  | 'primary'
  | 'gray';

export const MATCH_FEEDBACK = "Feedback has been collected"

export const MATCH_STATUS = {
  APPROVED: 'approved',
  REJECTED: 'declined',
  PENDING: 'pending',
  PURSUED: 'pursued',
  NOT_PURSUED: 'not_pursued',
  AWAITING_OTHER: 'awaiting_other',
  PASSED_BY_ME: 'passed_by_me',
  PASSED_BY_OTHER: 'passed_by_other',
  PASSED: 'passed',
};

export interface UpdateMatchStatusData {
  match_id: string;
  status: string;
}

export interface SubmitRejectionFeedbackData {
  match_id: string;
  feedback: string;
}

export interface GetNetworkMatchesData {
  /** Multi-event: scope the list to one event; omit for all events. */
  event_id?: string;
  page: number;
  limit: number;
  status: string;
  sub_status?: string;
  start_date?: string;
  end_date?: string;
}

export interface GetMatchesCountData {
  start_date?: string;
  end_date?: string;
  /** Multi-event: count only this event's matches (keeps tabs in step with the filter). */
  event_id?: string;
}

export interface MatchCounts {
  pending: number;
  approved: {
    approved: number;
    awaiting_other: number;
    total: number;
  };
  passed: {
    passed_by_me: number;
    passed_by_other: number;
    passed: number;
    total: number;
  };
}

// === Phase 1.1: Match Explanation Types ===
// Apr-17 fix: shape now matches AI service output from commit 166c406.
// AI returns a flat object with four integer scores (0-100).
// Previously this file declared a legacy 6-dim nested schema that no longer
// corresponds to production data — the Compatibility Breakdown UI rendered
// "0% overall" because it filtered on keys that never existed in the payload.
export interface ScoreBreakdown {
  role_fit: number;
  stage_match: number;
  industry_match: number;
  geography_match: number;
}

export interface MatchExplanation {
  match_id: string;
  compatibility_score: number;
  match_tier: 'perfect' | 'strong' | 'worth_exploring' | 'low';
  headline: string;
  key_points: string[];
  explanation: string;
  synergy_areas: string[];
  friction_points: string[];
  talking_points: string[];
  score_breakdown: ScoreBreakdown | null;
  // Apr-17 Phase 2b: primary_goal reciprocity flag from AI matching pipeline
  // (true = Primary, false = Adjacent, null = not evaluable)
  reciprocal?: boolean | null;
  generated_at: string;
}

// === Phase 1.2: Ice Breakers Types ===
export interface IceBreaker {
  id: string;
  match_id: string;
  user_id: string;
  suggestions: string[];
  selected_suggestion: number | null;
  used_at: string | null;
  created_at: string;
}

export interface IceBreakersResponse {
  ice_breakers: IceBreaker;
  match: {
    id: string;
    other_user_name: string;
    other_user_objective: string;
  };
}

export interface TrackIceBreakerUsageData {
  match_id: string;
  selected_index: number;
}

// === Phase 2.1: Feedback Learning Types ===
export type FeedbackReasonTag =
  | 'wrong_industry'
  | 'wrong_stage'
  | 'bad_timing'
  | 'not_relevant'
  | 'already_connected'
  | 'too_similar'
  | 'too_different'
  | 'location_mismatch'
  | 'other';

export interface SubmitMatchFeedbackWithReasonsData {
  match_id: string;
  status: string;
  reason_tags?: FeedbackReasonTag[];
  reason_text?: string;
}

// === Phase 2.3: Match Tier Types ===
export type MatchTier = 'perfect' | 'strong' | 'worth_exploring' | 'low';

export const MATCH_TIER_CONFIG: Record<
  MatchTier,
  { label: string; color: StatusColor; icon: string }
> = {
  perfect: { label: 'Perfect Match', color: 'success', icon: '✨' },
  strong: { label: 'Strong Match', color: 'primary', icon: '🎯' },
  worth_exploring: { label: 'Worth Exploring', color: 'info', icon: '🔍' },
  low: { label: 'Low Match', color: 'gray', icon: '📊' },
};
